import React, { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { Building2, User, MapPin, Landmark, Save, X } from 'lucide-react';
import { Button, Input, TextArea, Select, toast } from '../../components/common';
import { vendorApi } from '../../services/api';
import './VendorForm.css';

export default function VendorForm({ vendor, onSuccess, onCancel }) {
    const isEditing = !!vendor;

    const [form, setForm] = useState({
        companyName: '',
        vendorType: 'local',
        businessType: 'distributor',
        taxId: '',
        registrationNumber: '',
        contactPerson: '',
        email: '',
        phone: '',
        website: '',
        address: '',
        city: '',
        province: '',
        postalCode: '',
        country: 'Indonesia',
        bankName: '',
        bankBranch: '',
        accountNumber: '',
        accountName: ''
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (vendor) {
            setForm({
                companyName: vendor.companyName || '',
                vendorType: vendor.vendorType || 'local',
                businessType: vendor.businessType || 'distributor',
                taxId: vendor.taxId || '',
                registrationNumber: vendor.registrationNumber || '',
                contactPerson: vendor.contactPerson || '',
                email: vendor.email || '',
                phone: vendor.phone || '',
                website: vendor.website || '',
                address: vendor.address || '',
                city: vendor.city || '',
                province: vendor.province || '',
                postalCode: vendor.postalCode || '',
                country: vendor.country || 'Indonesia',
                bankName: vendor.bankAccounts?.[0]?.bankName || '',
                bankBranch: vendor.bankAccounts?.[0]?.branchName || '',
                accountNumber: vendor.bankAccounts?.[0]?.accountNumber || '',
                accountName: vendor.bankAccounts?.[0]?.accountName || ''
            });
        }
    }, [vendor]);

    const mutation = useMutation({
        mutationFn: (data) => isEditing
            ? vendorApi.update(vendor.id, data)
            : vendorApi.create(data),
        onSuccess: () => {
            toast.success(`Vendor ${isEditing ? 'updated' : 'created'} successfully`);
            onSuccess?.();
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Failed to save vendor');
        }
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validate = () => {
        const newErrors = {};
        if (!form.companyName) newErrors.companyName = 'Company name is required';
        if (!form.email) newErrors.email = 'Email is required';
        if (!form.contactPerson) newErrors.contactPerson = 'Contact person is required';
        if (!form.phone) newErrors.phone = 'Phone is required';
        setErrors(newErrors);

        if (Object.keys(newErrors).length > 0) {
            toast.error('Please fill in all required fields');
            return false;
        }
        return true;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validate()) return;

        // Send only vendor fields, exclude bank account fields
        const vendorData = {
            companyName: form.companyName,
            email: form.email,
            vendorType: form.vendorType || 'local',
            businessType: form.businessType || undefined,
            taxId: form.taxId || undefined,
            registrationNumber: form.registrationNumber || undefined,
            contactPerson: form.contactPerson || undefined,
            phone: form.phone || undefined,
            website: form.website || undefined,
            address: form.address || undefined,
            city: form.city || undefined,
            province: form.province || undefined,
            postalCode: form.postalCode || undefined,
            country: form.country || 'Indonesia'
        };

        // Remove undefined values
        Object.keys(vendorData).forEach(key => {
            if (vendorData[key] === undefined) {
                delete vendorData[key];
            }
        });

        console.log('Submitting vendor data:', vendorData);
        mutation.mutate(vendorData);
    };

    const vendorTypeOptions = [
        { value: 'local', label: 'Local' },
        { value: 'national', label: 'National' },
        { value: 'international', label: 'International' }
    ];

    const businessTypeOptions = [
        { value: 'manufacturer', label: 'Manufacturer' },
        { value: 'distributor', label: 'Distributor' },
        { value: 'retailer', label: 'Retailer' },
        { value: 'service_provider', label: 'Service Provider' },
        { value: 'contractor', label: 'Contractor' }
    ];

    return (
        <div className="vendor-form-page">
            {/* Header */}
            <div className="vendor-form-header">
                <div className="header-icon">
                    <Building2 size={28} />
                </div>
                <div className="header-text">
                    <h2>{isEditing ? 'Edit Vendor' : 'Register New Vendor'}</h2>
                    <p>{isEditing ? 'Update vendor information' : 'Fill in vendor details below'}</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="vendor-form-content">
                {/* Section 1: Company Information */}
                <div className="form-section">
                    <div className="section-header">
                        <div className="section-icon basic">
                            <Building2 size={18} />
                        </div>
                        <div className="section-title">
                            <h3>Company Information</h3>
                            <p>Basic company details and classification</p>
                        </div>
                    </div>
                    <div className="section-body">
                        <div className="form-grid">
                            <div className="form-field full">
                                <Input
                                    name="companyName"
                                    label="Company Name"
                                    placeholder="PT Example Technology Indonesia"
                                    value={form.companyName}
                                    onChange={handleChange}
                                    error={errors.companyName}
                                    required
                                />
                            </div>
                            <div className="form-field half">
                                <Select
                                    name="vendorType"
                                    label="Vendor Type"
                                    value={form.vendorType}
                                    onChange={handleChange}
                                    options={vendorTypeOptions}
                                />
                            </div>
                            <div className="form-field half">
                                <Select
                                    name="businessType"
                                    label="Business Type"
                                    value={form.businessType}
                                    onChange={handleChange}
                                    options={businessTypeOptions}
                                />
                            </div>
                            <div className="form-field half">
                                <Input
                                    name="taxId"
                                    label="Tax ID (NPWP)"
                                    placeholder="00.000.000.0-000.000"
                                    value={form.taxId}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="form-field half">
                                <Input
                                    name="registrationNumber"
                                    label="Registration Number"
                                    placeholder="NIB / SIUP Number"
                                    value={form.registrationNumber}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section 2: Contact Information */}
                <div className="form-section">
                    <div className="section-header">
                        <div className="section-icon contact">
                            <User size={18} />
                        </div>
                        <div className="section-title">
                            <h3>Contact Information</h3>
                            <p>Primary contact person details</p>
                        </div>
                    </div>
                    <div className="section-body">
                        <div className="form-grid">
                            <div className="form-field half">
                                <Input
                                    name="contactPerson"
                                    label="Contact Person"
                                    placeholder="John Doe"
                                    value={form.contactPerson}
                                    onChange={handleChange}
                                    error={errors.contactPerson}
                                    required
                                />
                            </div>
                            <div className="form-field half">
                                <Input
                                    name="email"
                                    type="email"
                                    label="Email Address"
                                    placeholder="contact@company.com"
                                    value={form.email}
                                    onChange={handleChange}
                                    error={errors.email}
                                    required
                                />
                            </div>
                            <div className="form-field half">
                                <Input
                                    name="phone"
                                    label="Phone Number"
                                    placeholder="+62 21 1234 5678"
                                    value={form.phone}
                                    onChange={handleChange}
                                    error={errors.phone}
                                    required
                                />
                            </div>
                            <div className="form-field half">
                                <Input
                                    name="website"
                                    label="Website"
                                    placeholder="https://www.company.com"
                                    value={form.website}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section 3: Address */}
                <div className="form-section">
                    <div className="section-header">
                        <div className="section-icon address">
                            <MapPin size={18} />
                        </div>
                        <div className="section-title">
                            <h3>Business Address</h3>
                            <p>Company office location</p>
                        </div>
                    </div>
                    <div className="section-body">
                        <div className="form-grid">
                            <div className="form-field full">
                                <TextArea
                                    name="address"
                                    label="Street Address"
                                    placeholder="Jl. Sudirman No. 123, Gedung ABC Lantai 5"
                                    value={form.address}
                                    onChange={handleChange}
                                    rows={2}
                                />
                            </div>
                            <div className="form-field quarter">
                                <Input
                                    name="city"
                                    label="City"
                                    placeholder="Jakarta Selatan"
                                    value={form.city}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="form-field quarter">
                                <Input
                                    name="province"
                                    label="Province"
                                    placeholder="DKI Jakarta"
                                    value={form.province}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="form-field quarter">
                                <Input
                                    name="postalCode"
                                    label="Postal Code"
                                    placeholder="12190"
                                    value={form.postalCode}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="form-field quarter">
                                <Input
                                    name="country"
                                    label="Country"
                                    value={form.country}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section 4: Bank Account */}
                <div className="form-section optional">
                    <div className="section-header">
                        <div className="section-icon bank">
                            <Landmark size={18} />
                        </div>
                        <div className="section-title">
                            <h3>Bank Account</h3>
                            <p>Payment information (optional)</p>
                        </div>
                        <span className="optional-tag">Optional</span>
                    </div>
                    <div className="section-body">
                        <div className="form-grid">
                            <div className="form-field half">
                                <Input
                                    name="bankName"
                                    label="Bank Name"
                                    placeholder="Bank Central Asia (BCA)"
                                    value={form.bankName}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="form-field half">
                                <Input
                                    name="bankBranch"
                                    label="Branch"
                                    placeholder="Sudirman Branch"
                                    value={form.bankBranch}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="form-field half">
                                <Input
                                    name="accountNumber"
                                    label="Account Number"
                                    placeholder="1234567890"
                                    value={form.accountNumber}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="form-field half">
                                <Input
                                    name="accountName"
                                    label="Account Name"
                                    placeholder="PT Example Technology Indonesia"
                                    value={form.accountName}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>
                        <p className="bank-note">
                            💡 Bank account can be added or updated after vendor registration
                        </p>
                    </div>
                </div>

                {/* Form Actions */}
                <div className="vendor-form-actions">
                    <Button
                        variant="secondary"
                        type="button"
                        onClick={onCancel}
                    >
                        <X size={18} />
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        loading={mutation.isPending}
                    >
                        <Save size={18} />
                        {isEditing ? 'Update Vendor' : 'Register Vendor'}
                    </Button>
                </div>
            </form>
        </div>
    );
}

VendorForm.propTypes = {
    vendor: PropTypes.object,
    onSuccess: PropTypes.func,
    onCancel: PropTypes.func
};
